import React, { useRef } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-regular-svg-icons";

/**
 * DS DateInput — figma date 디자인 + 실제 인터랙션은 native input type="date".
 * (StockList.js의 검색옵션 date input 패턴 참조)
 *
 * 시각: figma 디자인(아이콘 + 라벨 + 연도/월/일 단위 표시).
 * 동작: 영역 클릭 시 native picker 열림 → 변경 즉시 onChange.
 *
 * suspect: figma date input radius가 5px (다른 컴포넌트는 6px). 일관성 차이.
 *
 * @param {object} props
 * @param {string} props.label - "시작일" / "종료일"
 * @param {Date|null} [props.value]
 * @param {(date: Date|null) => void} props.onChange
 * @param {string} [props.min] - 선택 가능 최소일 "YYYY-MM-DD". 미전달이면 제한 없음.
 * @param {string} [props.max] - 선택 가능 최대일 "YYYY-MM-DD". 미전달이면 제한 없음.
 * @param {string} [props.className]
 */
const DSDateInput = ({
  label,
  value,
  onChange,
  min,
  max,
  className = "",
}) => {
  const inputRef = useRef(null);
  const y = value ? String(value.getFullYear()) : "";
  const m = value ? String(value.getMonth() + 1).padStart(2, "0") : "";
  const d = value ? String(value.getDate()).padStart(2, "0") : "";
  const filled = !!value;

  const handleAreaClick = () => {
    const input = inputRef.current;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      input.showPicker();
    } else {
      input.focus();
      input.click();
    }
  };

  const handleNativeChange = (e) => {
    const raw = e.target.value;
    if (!onChange) return;
    onChange(raw ? new Date(raw) : null);
  };

  return (
    <div
      className={`ds-date ${className}`}
      onClick={handleAreaClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleAreaClick();
      }}
    >
      <span className="ds-date__label">
        <FontAwesomeIcon icon={faCalendar} />
        <span>{label}</span>
      </span>
      <span className="ds-date__fields">
        <span
          className={`ds-date__field ds-date__field--year ${
            filled ? "ds-date__field--filled" : ""
          }`}
        >
          {y || "연도."}
        </span>
        <span
          className={`ds-date__field ds-date__field--month ${
            filled ? "ds-date__field--filled" : ""
          }`}
        >
          {m || "월."}
        </span>
        <span
          className={`ds-date__field ds-date__field--day ${
            filled ? "ds-date__field--filled" : ""
          }`}
        >
          {d || "일."}
        </span>
      </span>
      <input
        ref={inputRef}
        type="date"
        className="ds-date__native"
        value={value ? moment(value).format("YYYY-MM-DD") : ""}
        min={min}
        max={max}
        onChange={handleNativeChange}
        aria-label={label}
        // suspect: native input을 absolute로 가려놨지만 일부 브라우저에서 hover 시 표시될 수 있음. 차후 확인.
      />
    </div>
  );
};

export default React.memo(DSDateInput);
